import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef } from "react";
import { Knopf } from "./Knopf";
import { useWeltAktiv } from "../welt/WeltWurzel";

/** W73c (D-284, D-285): Rueckfrage als zentrierter Dialog wie die Abfrage der App (iOS `alert`), auch mobil, statt des
 *  Blatts von unten: Titel, ein Satz, „Behalten“ (Fokus beim Oeffnen, die Wahl ohne Folgen) und die rote Handlung. Radix
 *  liefert role="dialog", Fokusfalle und Escape. Gesperrt (waehrend des Sendens, D-089): nichts schliesst, beide Knoepfe
 *  aria-disabled, der Fokus bleibt im Dialog. W73d (D-311 F1): Titel und Satz rollen in einem eigenen Bereich, die Knoepfe
 *  stehen fest unten im Dialog; der Dialog ist hoechstens so hoch wie die sichtbare Hoehe minus Rand. */
export function Rueckfrage(props: {
  offen: boolean;
  titel: string;
  text: string;
  behalten: string;
  handlung: string;
  gesperrt?: boolean;
  onHandlung: () => void;
  onSchliessen: () => void;
  kennung?: string;
  /** W82 Web-5h1: falsch, solange Zeiger-Tipps auf die Handlung noch nicht zählen (Doppeltipp auf den Auslöser, Memory
   *  `doppeltipp-dialog-probe`); die Tastatur gilt immer. Fehlt: wie bisher. `data-bereit` zeigt den Stand. */
  zeigerBereit?: boolean;
  /** W82 Web-5h1: Fokusziel nach dem Schließen (die nachrückende Zeile nach dem Löschen); null: wie bisher */
  fokusNachher?: () => HTMLElement | null;
  /** W82 Beta-W: Gestalt der Handlung; Vorgabe die rote (Löschen, Verwerfen), „primaer“ für eine Handlung ohne Verlust des
   *  Texts („Erledigt“ auf der Seite „Rückmeldungen“). */
  handlungArt?: "rot" | "primaer";
  /** R1 W82 Web-Beta-W2 (D-733, Frage 97 = A): Gestalt der sicheren Wahl; Vorgabe die leise („sekundaer“), „primaer“ macht sie
   *  zum Akzent-Knopf der Welt (heller Knopf, `--pgw-knopf-hell`), die Handlung bleibt dann die ruhige Glas-Pille ohne Koralle
   *  (`handlungArt` „rot“, welt.css); die Warnung trägt der Satz. */
  behaltenArt?: "sekundaer" | "primaer";
  /** W82 Beta-W2 (D-707): nur vorgelesen nach dem Satz, etwa wovon die Rede ist („Export vom …“); fehlt: wie bisher */
  lesetext?: string;
}) {
  const { gesperrt = false } = props;
  // W82 Teil 3: unter einer WeltWurzel trägt der Dialog im Portal selbst data-welt und die W82-Gestalt (welt.css)
  const welt = useWeltAktiv();
  const ausloeser = useRef<HTMLElement | null>(null);
  // sofort ab dem Tipp auf die Handlung gesperrt, nicht erst mit dem naechsten Rendern (ein Escape gleich danach schloesse
  // sonst, waehrend die Anfrage laeuft); frei wieder mit dem Ende der Sperre von aussen oder beim Schliessen
  const sendet = useRef(false);
  const warGesperrt = useRef(gesperrt);
  useEffect(() => {
    if (props.offen && document.activeElement instanceof HTMLElement) ausloeser.current = document.activeElement;
    if (!props.offen) sendet.current = false;
  }, [props.offen]);
  useEffect(() => {
    if (warGesperrt.current && !gesperrt) sendet.current = false;
    warGesperrt.current = gesperrt;
  }, [gesperrt]);
  const zu = () => gesperrt || sendet.current;
  // W82 Web-5h-N2 a11 (R3 O-1): rollen Titel und Satz (320 px bei 200 %), zeigt eine Kante am Rand des Rollbereichs, dass weiter
  // unten (`data-mehr`) oder oben (`data-gerollt`) Text steht; ohne Rollen keine Kante, keine Bewegung (bausteine.css)
  const rollbereich = (el: HTMLDivElement | null) => {
    if (!el) return;
    const messen = () => {
      el.toggleAttribute("data-mehr", el.scrollTop + el.clientHeight < el.scrollHeight - 1);
      el.toggleAttribute("data-gerollt", el.scrollTop > 0);
    };
    messen();
    // ohne ResizeObserver (jsdom, sehr alte Browser) nur beim Öffnen und beim Rollen
    const beobachter = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(messen);
    beobachter?.observe(el);
    for (const kind of el.children) beobachter?.observe(kind);
    el.addEventListener("scroll", messen, { passive: true });
    return () => {
      beobachter?.disconnect();
      el.removeEventListener("scroll", messen);
    };
  };
  const schliessen = () => {
    if (!zu()) props.onSchliessen();
  };
  return (
    <Dialog.Root open={props.offen} onOpenChange={(auf) => !auf && schliessen()}>
      <Dialog.Portal>
        <Dialog.Overlay data-welt={welt ? "" : undefined} className={`fixed inset-0 z-60 bg-pg-overlay ${welt ? "rueckfrage-schleier" : ""}`} />
        <Dialog.Content
          data-testid={props.kennung}
          data-welt={welt ? "" : undefined}
          data-bereit={props.zeigerBereit === false ? undefined : ""}
          className={`fixed top-1/2 left-1/2 z-70 flex max-h-(--rueckfrage-max-h) w-(--w-rueckfrage) max-w-(--dialog-max-w) -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-hidden rounded-(--r-dialog) bg-pg-bg p-4 shadow-sheet ${welt ? "rueckfrage-welt" : ""}`}
          onEscapeKeyDown={(e) => zu() && e.preventDefault()}
          onInteractOutside={(e) => zu() && e.preventDefault()}
          onCloseAutoFocus={(e) => {
            // Ist der Ausloeser weg (die geloeschte Zeile) oder war es `body` (W73d, D-314 H2), auf die Ueberschrift der Seite
            e.preventDefault();
            const a = ausloeser.current;
            const nachher = props.fokusNachher?.();
            const ziel = nachher?.isConnected ? nachher : a?.isConnected && a !== document.body ? a : document.querySelector<HTMLElement>("main h1");
            ziel?.focus({ preventScroll: true });
          }}
        >
          {/* nur Titel und Satz rollen (320 px bei 200 %: der Satz ist hoeher als das Fenster); der Rand p-4 des Dialogs haelt
              den Fokusring des unteren Knopfs ganz im Dialog */}
          <div ref={rollbereich} className="rueckfrage-text flex min-h-0 flex-col gap-1 overflow-y-auto overscroll-contain text-center">
            <Dialog.Title className="t-headline break-words hyphens-auto">{props.titel}</Dialog.Title>
            <Dialog.Description className="t-subheadline break-words hyphens-auto text-pg-text-2">
              {props.text}
              {props.lesetext && <span className="sr-only">{` ${props.lesetext}`}</span>}
            </Dialog.Description>
          </div>
          {/* nebeneinander gleich breit; passt ein Wort nicht mehr (320 px bei 200 %), untereinander und notfalls getrennt
              statt ueberdeckt (Verifier V73c R1: mit min-w-0 schrumpften beide Knoepfe auf 71 px, die Woerter liefen ineinander) */}
          <div className="flex shrink-0 flex-wrap gap-2 [&>*]:min-w-fit [&>*]:flex-1">
            <Knopf art={props.behaltenArt ?? "sekundaer"} className="hyphens-auto" aria-disabled={gesperrt || undefined} onClick={schliessen}>
              {props.behalten}
            </Knopf>
            <Knopf
              art={props.handlungArt ?? "rot"}
              className="hyphens-auto"
              aria-disabled={gesperrt || undefined}
              onClick={(e) => {
                if (zu() || (props.zeigerBereit === false && e.detail > 0)) return;
                sendet.current = true;
                props.onHandlung();
              }}
            >
              {props.handlung}
            </Knopf>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
